/**
 * Login Page - T040
 */
import { useState } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useAuth, useLogin } from '../hooks/useAuth';
import { GlassPanel, Input, Button, LoadingSpinner } from '../components/ui';
import { getBuildInfo } from '@armard/shared';

const buildInfo = getBuildInfo({
  channel: import.meta.env.VITE_BUILD_CHANNEL,
  revision: import.meta.env.VITE_BUILD_REVISION,
  buildNumber: import.meta.env.VITE_BUILD_NUMBER,
});

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Redirect if already authenticated
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Bitte E-Mail und Passwort eingeben');
      return;
    }

    try {
      await login.mutateAsync({ email, password });
    } catch {
      setError('Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-sm"
      >
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-32 h-32 border border-white/10 rounded-xl mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="21" viewBox="0 0 42 22">
              <path
                fill="currentColor"
                className="text-white/80"
                d="M10.825,0.825 C17.418,0.825 24.012,0.825 30.605,0.825 C30.605,4.121 30.605,7.419 30.605,10.715 C24.012,10.715 17.418,10.715 10.825,10.715 C10.825,7.419 10.825,4.121 10.825,0.825 ZM0.935,10.715 C4.231,10.715 7.529,10.715 10.825,10.715 C10.825,14.011 10.825,17.309 10.825,20.605 C7.529,20.605 4.231,20.605 0.935,20.605 C0.935,17.309 0.935,14.011 0.935,10.715 ZM30.605,10.715 C33.901,10.715 37.199,10.715 40.495,10.715 C40.495,14.011 40.495,17.309 40.495,20.605 C37.199,20.605 33.901,20.605 30.605,20.605 C30.605,17.309 30.605,14.011 30.605,10.715 Z"
              />
            </svg>
          </div>
          <div className="flex flex-col items-center">
            <h1 className="text-3xl font-bold text-white tracking-tight">Armard</h1>
            <span className="text-xs text-white/30 tracking-widest uppercase mt-0.5">CRM</span>
          </div>
        </div>

        {/* Login form */}
        <GlassPanel variant="elevated" padding="lg">
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="E-Mail"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              autoComplete="email"
              autoFocus
            />

            <Input
              label="Passwort"
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-lg bg-red-500/10 border border-red-500/20"
              >
                <p className="text-sm text-red-400">{error}</p>
              </motion.div>
            )}

            <Button type="submit" variant="primary" className="w-full" loading={login.isPending}>
              Anmelden
            </Button>
          </form>
        </GlassPanel>

        {/* Footer */}
        <div className="text-center text-white/30 text-xs mt-6 space-y-1">
          <p className="break-all" title={`Revision: ${buildInfo.revision}`}>
            v{buildInfo.version}
          </p>
          <div className="flex items-center justify-center gap-2">
            <a
              href="https://armadaquadrat.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white/50 transition-colors"
            >
              armadaquadrat.com
            </a>
            <span>·</span>
            <a
              href="https://armadaquadrat.com/impressum"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white/50 transition-colors"
            >
              Impressum
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
